/* PPE Parts & Supply: customer accounts, password page and gift card. No dependencies.
   Contracts:
   - Recover password: #recover.auth-recover / #login.auth-login siblings, links [data-recover-toggle][href="#recover"|"#login"],
     headings [data-auth-heading]. Without JS the panels switch with :target (see section-customer.css).
   - Address forms: select[data-address-country][data-default][data-province-id][data-province-container],
     province select[data-default]. Options come from all_country_option_tags (data-provinces JSON).
   - Address edit: button[data-address-toggle][aria-controls] toggles .is-open on the panel and .is-editing on [data-address-card].
   - Address delete: form[data-address-delete][data-confirm] (contains _method=delete).
   - Order filter: select[data-order-filter][aria-controls=<table id>], rows tr[data-status], [data-order-filter-empty=<table id>].
   - Gift card: [data-gift-card-qr][data-identifier], button[data-copy-code][data-copy-target][data-copied], [data-copy-status], button[data-print]. */
(function () {
  'use strict';

  if (window.customerScriptLoaded) return;
  window.customerScriptLoaded = true;

  const onReady = (fn) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  };

  /* ------------------------------------------------------------------
     Recover password panel
     ------------------------------------------------------------------ */
  const initRecover = () => {
    const recover = document.getElementById('recover');
    const login = document.getElementById('login');
    if (!recover || !login) return;

    const show = (showRecover, focus) => {
      recover.classList.toggle('is-active', showRecover);
      if (!focus) return;
      const panel = showRecover ? recover : login;
      const heading = panel.querySelector('[data-auth-heading]');
      if (heading) heading.focus();
    };

    if (window.location.hash === '#recover') show(true, false);

    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-recover-toggle]');
      if (!toggle) return;
      event.preventDefault();
      const toRecover = toggle.getAttribute('href') === '#recover';
      show(toRecover, true);
      const url = `${window.location.pathname}${window.location.search}${toRecover ? '#recover' : ''}`;
      window.history.replaceState(null, '', url);
    });

    window.addEventListener('hashchange', () => {
      if (window.location.hash === '#recover') show(true, true);
      else if (window.location.hash === '#login') show(false, true);
    });
  };

  /* ------------------------------------------------------------------
     Country / province selects
     ------------------------------------------------------------------ */
  const selectValue = (select, value) => {
    if (!value) return false;
    const match = Array.from(select.options).find((option) => option.value === value || option.textContent.trim() === value);
    if (match) {
      select.value = match.value;
      return true;
    }
    return false;
  };

  const initCountrySelects = () => {
    document.querySelectorAll('select[data-address-country]').forEach((country) => {
      if (country.dataset.bound) return;
      country.dataset.bound = 'true';
      const province = document.getElementById(country.dataset.provinceId);
      const container = document.getElementById(country.dataset.provinceContainer);
      if (!province) return;

      if (!selectValue(country, country.dataset.default)) country.selectedIndex = Math.max(country.selectedIndex, 0);

      const update = (useDefault) => {
        const option = country.options[country.selectedIndex];
        let provinces = [];
        try {
          provinces = JSON.parse((option && option.getAttribute('data-provinces')) || '[]');
        } catch (e) {
          provinces = [];
        }
        province.innerHTML = '';
        if (!provinces.length) {
          if (container) container.hidden = true;
          return;
        }
        provinces.forEach(([value, label]) => province.add(new Option(label || value, value)));
        if (container) container.hidden = false;
        if (useDefault) selectValue(province, province.dataset.default);
      };

      update(true);
      country.addEventListener('change', () => update(false));
    });
  };

  /* ------------------------------------------------------------------
     Address edit toggles and delete confirmation
     ------------------------------------------------------------------ */
  const initAddresses = () => {
    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-address-toggle]');
      if (!toggle) return;
      const id = toggle.getAttribute('aria-controls');
      const panel = document.getElementById(id);
      if (!panel) return;
      const open = !panel.classList.contains('is-open');
      panel.classList.toggle('is-open', open);
      document.querySelectorAll(`[data-address-toggle][aria-controls="${id}"]`).forEach((button) => {
        button.setAttribute('aria-expanded', String(open));
      });
      const card = toggle.closest('[data-address-card]');
      if (card) card.classList.toggle('is-editing', open);

      if (open) {
        const field = panel.querySelector('input:not([type="hidden"]), select');
        if (field) field.focus();
      } else if (card) {
        const opener = card.querySelector(`.addresses-card__actions [data-address-toggle][aria-controls="${id}"]`);
        if (opener) opener.focus();
      }
    });

    document.addEventListener('submit', (event) => {
      const form = event.target.closest('form[data-address-delete]');
      if (!form) return;
      if (!window.confirm(form.dataset.confirm || '')) event.preventDefault();
    });
  };

  /* ------------------------------------------------------------------
     Order history status filter (current page only)
     ------------------------------------------------------------------ */
  const initOrderFilter = () => {
    document.querySelectorAll('select[data-order-filter]').forEach((select) => {
      const tableId = select.getAttribute('aria-controls');
      const table = document.getElementById(tableId);
      if (!table) return;
      const rows = Array.from(table.querySelectorAll('tbody tr[data-status]'));
      const empty = document.querySelector(`[data-order-filter-empty="${tableId}"]`);

      const apply = () => {
        const value = select.value;
        let shown = 0;
        rows.forEach((row) => {
          const match = value === 'all' || row.dataset.status === value;
          row.hidden = !match;
          if (match) shown += 1;
        });
        if (empty) empty.hidden = shown > 0;
      };

      select.addEventListener('change', apply);
      if (select.value !== 'all') apply();
    });
  };

  /* ------------------------------------------------------------------
     Gift card: QR code, copy code, print
     ------------------------------------------------------------------ */
  const initGiftCard = () => {
    const qr = document.querySelector('[data-gift-card-qr]');
    if (qr && typeof window.QRCode === 'function' && !qr.dataset.rendered) {
      qr.dataset.rendered = 'true';
      // eslint-disable-next-line no-new
      new window.QRCode(qr, {
        text: qr.dataset.identifier,
        width: 120,
        height: 120,
        imageAltText: qr.dataset.alt || ''
      });
    }

    const copy = document.querySelector('[data-copy-code]');
    if (copy) {
      if (!navigator.clipboard || !window.isSecureContext) {
        copy.hidden = true;
      } else {
        copy.addEventListener('click', async () => {
          const target = document.getElementById(copy.dataset.copyTarget);
          const status = document.querySelector('[data-copy-status]');
          if (!target) return;
          try {
            await navigator.clipboard.writeText(target.textContent.trim());
            if (status) {
              status.textContent = '';
              window.setTimeout(() => { status.textContent = copy.dataset.copied || ''; }, 50);
            }
          } catch (e) {
            /* clipboard permission denied, so the code stays visible for manual copy */
          }
        });
      }
    }

    document.querySelectorAll('[data-print]').forEach((button) => {
      button.addEventListener('click', () => window.print());
    });
  };

  onReady(() => {
    initRecover();
    initCountrySelects();
    initAddresses();
    initOrderFilter();
    initGiftCard();
  });

  document.addEventListener('shopify:section:load', () => {
    initCountrySelects();
    initOrderFilter();
  });
})();
